import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  getCurrencies, deleteCurrency, toggleCurrencyStatus, clearError, clearSuccess,
} from '../../../../redux/Slices/authSlice';
import {
  FaPlus, FaEdit, FaTrash, FaEye, FaToggleOn, FaToggleOff, FaPrint, FaFileExcel,
  FaSearch, FaFilter, FaSort, FaSortUp, FaSortDown,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';
import usePdfExport from '../../../../shared/pdf/usePdfExport';
import PdfExportManager from '../../../../shared/pdf/PdfExportManager';
import { exportRowsToExcel } from '../../../../shared/utils.js/excelExport';

const SEARCH_DEBOUNCE_MS = 400;
const SORT_FIELDS = {
  index: 'index',
  name: 'name',
  code: 'code',
  symbol: 'symbol',
  status: 'is_active',
  isBase: 'is_base_currency',
};

const Currencies = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();

  const { isLoading, error, success } = useSelector(state => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [isBaseFilter, setIsBaseFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currencyToDelete, setCurrencyToDelete] = useState(null);
  const [allCurrencies, setAllCurrencies] = useState([]);

  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });

  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    setTitle('sidenav.currencies');
  }, [setTitle]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCurrencies = useCallback(async () => {
    try {
      const result = await dispatch(getCurrencies({ page: 1, per_page: 1000 }));
      if (getCurrencies.fulfilled.match(result)) {
        const response = result.payload;
        const data = response?.data || response;
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        setAllCurrencies(list);
      }
    } catch {
      /* errors surface via the auth slice */
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies, i18n.language]);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL, autoClose: 3000 });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
      fetchCurrencies();
    }
  }, [success, dispatch, isRTL, fetchCurrencies]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL, autoClose: 5000 });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  // ─── search + filters + sort (client-side) ─────────────────────────────
  const filteredCurrencies = useMemo(() => {
    let result = allCurrencies || [];

    if (debouncedSearch) {
      const term = convertArabicNumbersToEnglish(debouncedSearch)
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      result = result.filter(c => {
        const check = v =>
          convertArabicNumbersToEnglish(String(v || ''))
            .toLowerCase()
            .includes(term);
        return check(c.name) || check(c.code) || check(c.symbol);
      });
    }

    if (statusFilter !== '') {
      const activeVal = statusFilter === 'active';
      result = result.filter(c => Boolean(c.is_active) === activeVal);
    }

    if (isBaseFilter !== '') {
      const baseVal = isBaseFilter === 'yes';
      result = result.filter(c => Boolean(c.is_base_currency) === baseVal);
    }

    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal;
        let bVal;
        if (sortField === SORT_FIELDS.index) {
          aVal = a.id ?? 0;
          bVal = b.id ?? 0;
        } else {
          aVal = a[sortField] ?? '';
          bVal = b[sortField] ?? '';
        }
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [allCurrencies, debouncedSearch, statusFilter, isBaseFilter, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredCurrencies.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedCurrencies = filteredCurrencies.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );

  const handleApplyPerPage = () => {
    const validValue = Math.max(
      1,
      Math.min(perPageInput, filteredCurrencies?.length || 1000),
    );
    setPerPage(validValue);
    setCurrentPage(1);
  };

  const handleSort = field => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = field => {
    if (sortField !== field)
      return <FaSort style={{ opacity: 0.4, marginInlineStart: '5px' }} />;
    return sortOrder === 'asc' ? (
      <FaSortUp style={{ color: '#f0b429', marginInlineStart: '5px' }} />
    ) : (
      <FaSortDown style={{ color: '#f0b429', marginInlineStart: '5px' }} />
    );
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setDebouncedSearch(search); // flush debounce immediately
  };

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatusFilter('');
    setIsBaseFilter('');
    setSortField('');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(
    debouncedSearch || statusFilter !== '' || isBaseFilter !== '',
  );

  const handleDelete = currency => {
    setCurrencyToDelete(currency);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (currencyToDelete) {
      await dispatch(deleteCurrency(currencyToDelete.id));
      setShowDeleteModal(false);
      setCurrencyToDelete(null);
      fetchCurrencies();
    }
  };

  const handleToggleStatus = id => {
    dispatch(toggleCurrencyStatus(id));
  };

  // ─── Shared PDF / Excel column config ──────────────────────────────────
  const currencyStatus = c =>
    c.is_active
      ? t('currencies.status.active', { defaultValue: 'Active' })
      : t('currencies.status.inactive', { defaultValue: 'Inactive' });
  const currencyIsBase = c =>
    c.is_base_currency
      ? t('currencies.yes', { defaultValue: 'Yes' })
      : t('currencies.no', { defaultValue: 'No' });

  const exportColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('currencies.table.name', { defaultValue: 'Name' }),
        align: 'start',
      },
      { key: 'code', label: t('currencies.table.code', { defaultValue: 'Code' }) },
      {
        key: 'symbol',
        label: t('currencies.table.symbol', { defaultValue: 'Symbol' }),
      },
      {
        key: 'is_active',
        label: t('currencies.table.status', { defaultValue: 'Status' }),
        render: currencyStatus,
        excelValue: currencyStatus,
      },
      {
        key: 'is_base_currency',
        label: t('currencies.table.isBase', { defaultValue: 'Base Currency' }),
        render: currencyIsBase,
        excelValue: currencyIsBase,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isRTL],
  );

  const pdf = usePdfExport({
    idPrefix: 'currencies',
    isRTL,
    fileNamePrefix: 'ACE_Currencies',
    onError: err =>
      toast.error(
        err?.message === 'NO_DATA'
          ? t('currencies.print.noData', {
              defaultValue: 'No data available for printing',
            })
          : t('currencies.print.error', {
              defaultValue: 'An error occurred while creating the PDF',
            }),
        { rtl: isRTL },
      ),
  });

  const handleExportCurrencyToExcel = currency => {
    exportRowsToExcel({
      columns: exportColumns,
      rows: [currency],
      fileName: `${t('currencies.excel.fileName', { defaultValue: 'Currency' })}_${currency.name || currency.id}`,
      sheetName: t('currencies.excel.sheetName', { defaultValue: 'Currency' }),
      rtl: isRTL,
    });
    toast.success(
      t('currencies.export.success', { defaultValue: 'Data exported successfully' }),
      { rtl: isRTL },
    );
  };

  const handleExportAllToExcel = () => {
    if (!filteredCurrencies || filteredCurrencies.length === 0) {
      toast.error(
        t('currencies.export.noData', { defaultValue: 'No data to export' }),
        { rtl: isRTL },
      );
      return;
    }
    exportRowsToExcel({
      columns: exportColumns,
      rows: filteredCurrencies,
      fileName: t('currencies.excel.fileName', { defaultValue: 'Currencies' }),
      sheetName: t('currencies.excel.sheetName', { defaultValue: 'Currencies' }),
      rtl: isRTL,
    });
    toast.success(
      t('currencies.export.success', { defaultValue: 'Data exported successfully' }),
      { rtl: isRTL },
    );
  };

  // Applied-filters summary shown in the PDF header
  const filtersSummary = useMemo(() => {
    const parts = [];
    if (debouncedSearch)
      parts.push(
        `${t('currencies.search.label', { defaultValue: 'بحث' })}: ${debouncedSearch}`,
      );
    if (statusFilter !== '')
      parts.push(
        `${t('currencies.filter.status', { defaultValue: 'Status' })}: ${
          statusFilter === 'active'
            ? t('currencies.status.active', { defaultValue: 'Active' })
            : t('currencies.status.inactive', { defaultValue: 'Inactive' })
        }`,
      );
    if (isBaseFilter !== '')
      parts.push(
        `${t('currencies.filter.isBase', { defaultValue: 'Base Currency' })}: ${
          isBaseFilter === 'yes'
            ? t('currencies.yes', { defaultValue: 'Yes' })
            : t('currencies.no', { defaultValue: 'No' })
        }`,
      );
    return parts.join(' | ') || undefined;
  }, [debouncedSearch, statusFilter, isBaseFilter, t]);

  // Single-record PDF content — key/value grid (same layout as before)
  const renderSingleCurrency = currency => (
    <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '10px',
          fontSize: '13px',
          lineHeight: 1.6,
          direction: dir,
          textAlign,
        }}
      >
        <div>
          <strong>{t('currencies.print.name', { defaultValue: 'Name' })}:</strong>{' '}
          {currency.name || '—'}
        </div>
        <div>
          <strong>{t('currencies.print.code', { defaultValue: 'Code' })}:</strong>{' '}
          {currency.code || '—'}
        </div>
        <div>
          <strong>{t('currencies.print.symbol', { defaultValue: 'Symbol' })}:</strong>{' '}
          {currency.symbol || '—'}
        </div>
        <div>
          <strong>{t('currencies.print.status', { defaultValue: 'Status' })}:</strong>{' '}
          {currencyStatus(currency)}
        </div>
        <div>
          <strong>
            {t('currencies.print.isBase', { defaultValue: 'Base Currency' })}:
          </strong>{' '}
          {currencyIsBase(currency)}
        </div>
      </div>
    </div>
  );

  if (isLoading && (!allCurrencies || allCurrencies.length === 0)) {
    return (
      <div
        style={{
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          direction: dir,
        }}
      >
        {t('common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  const noData = !filteredCurrencies || filteredCurrencies.length === 0;

  return (
    <>
      <div
        style={{
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          direction: dir,
        }}
      >
        {/* ── Top Bar ── */}
        <div style={{ marginBottom: '30px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <h1
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'white',
                margin: 0,
                textAlign,
              }}
            >
              {t('currencies.title', { defaultValue: 'Currencies' })}
            </h1>

            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {/* Per-page selector */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                <label
                  style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}
                >
                  {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={() => setPerPageInput(Math.max(1, perPageInput - 1))}
                    style={{
                      backgroundColor: '#1b3350',
                      color: 'white',
                      border: '1px solid #333',
                      padding: '6px 10px',
                      borderRadius: '6px 0 0 6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      minWidth: '32px',
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={perPageInput}
                    onChange={e =>
                      setPerPageInput(
                        Math.max(1, Math.min(parseInt(e.target.value, 10) || 1, 1000)),
                      )
                    }
                    style={{
                      width: '70px',
                      padding: '6px 10px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderRadius: 0,
                      color: 'white',
                      fontSize: '14px',
                      textAlign: 'center',
                    }}
                  />
                  <button
                    onClick={() => setPerPageInput(Math.min(1000, perPageInput + 1))}
                    style={{
                      backgroundColor: '#1b3350',
                      color: 'white',
                      border: '1px solid #333',
                      padding: '6px 10px',
                      borderRadius: '0 6px 6px 0',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      minWidth: '32px',
                    }}
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={handleApplyPerPage}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('common.apply', { defaultValue: 'تطبيق' })}
                </button>
              </div>

              <button
                onClick={() => pdf.previewAll(filteredCurrencies)}
                disabled={noData}
                style={{
                  backgroundColor: noData ? '#666' : '#444',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaEye />{' '}
                {t('currencies.preview.all', { defaultValue: 'Preview PDF All' })}
              </button>

              <button
                onClick={() => pdf.printAll(filteredCurrencies)}
                disabled={pdf.isGeneratingPdf || noData}
                style={{
                  backgroundColor:
                    pdf.isGeneratingPdf || noData ? '#666' : '#1f5ff1',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: pdf.isGeneratingPdf || noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaPrint />
                {pdf.isGeneratingPdf
                  ? t('currencies.print.generating', { defaultValue: 'Generating...' })
                  : t('currencies.print.all', { defaultValue: 'Print PDF All' })}
              </button>

              <button
                onClick={handleExportAllToExcel}
                disabled={noData}
                style={{
                  backgroundColor: noData ? '#666' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaFileExcel />
                {t('currencies.export.all', { defaultValue: 'Export Excel' })}
              </button>

              <button
                onClick={() => navigate('/currencies/add')}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaPlus /> {t('currencies.add', { defaultValue: 'Add Currency' })}
              </button>
            </div>
          </div>

          {/* ── Search + Filters bar ── */}
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: '12px',
            }}
          >
            <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  [isRTL ? 'right' : 'left']: '12px',
                  color: '#888',
                  fontSize: '14px',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('currencies.search.placeholder', {
                  defaultValue: 'ابحث بالاسم أو الكود أو الرمز...',
                })}
                aria-label={t('currencies.search.placeholder', {
                  defaultValue: 'Search',
                })}
                style={{
                  width: '100%',
                  padding: '10px 40px',
                  paddingInlineStart: '40px',
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  direction: dir,
                  textAlign,
                }}
              />
              {search && (
                <button
                  onClick={resetFilters}
                  aria-label={t('common.reset', { defaultValue: 'Reset' })}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    [isRTL ? 'left' : 'right']: '10px',
                    background: 'none',
                    border: 'none',
                    color: '#aaa',
                    cursor: 'pointer',
                    fontSize: '16px',
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>

            <button
              onClick={() => setShowFilters(prev => !prev)}
              style={{
                backgroundColor: showFilters ? '#AC2000' : '#1b3350',
                color: 'white',
                border: '1px solid #444',
                padding: '10px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                flexDirection: isRTL ? 'row' : 'row-reverse',
                whiteSpace: 'nowrap',
              }}
            >
              <FaFilter />
              {t('common.filters', { defaultValue: 'Filters' })}
              {hasActiveFilters && (
                <span
                  style={{
                    backgroundColor: '#f0b429',
                    color: '#000',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 'bold',
                  }}
                >
                  !
                </span>
              )}
            </button>
          </div>

          {/* ── Filter Panel ── */}
          {showFilters && (
            <div
              style={{
                backgroundColor: '#1b3350',
                border: '1px solid #444',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '12px',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  minWidth: '160px',
                }}
              >
                <label style={{ color: '#ccc', fontSize: '13px' }}>
                  {t('currencies.filter.status', { defaultValue: 'Status' })}
                </label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  style={{
                    backgroundColor: '#1a1f2e',
                    color: 'white',
                    border: '1px solid #555',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    fontSize: '13px',
                  }}
                >
                  <option value="">
                    {t('currencies.filter.allStatuses', {
                      defaultValue: 'All Statuses',
                    })}
                  </option>
                  <option value="active">
                    {t('currencies.status.active', { defaultValue: 'Active' })}
                  </option>
                  <option value="inactive">
                    {t('currencies.status.inactive', { defaultValue: 'Inactive' })}
                  </option>
                </select>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  minWidth: '180px',
                }}
              >
                <label style={{ color: '#ccc', fontSize: '13px' }}>
                  {t('currencies.filter.isBase', { defaultValue: 'Base Currency' })}
                </label>
                <select
                  value={isBaseFilter}
                  onChange={e => setIsBaseFilter(e.target.value)}
                  style={{
                    backgroundColor: '#1a1f2e',
                    color: 'white',
                    border: '1px solid #555',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    fontSize: '13px',
                  }}
                >
                  <option value="">
                    {t('currencies.filter.all', { defaultValue: 'All' })}
                  </option>
                  <option value="yes">
                    {t('currencies.yes', { defaultValue: 'Yes' })}
                  </option>
                  <option value="no">
                    {t('currencies.no', { defaultValue: 'No' })}
                  </option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={applyFilters}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                  }}
                >
                  {t('common.apply', { defaultValue: 'Apply' })}
                </button>
                <button
                  onClick={resetFilters}
                  style={{
                    backgroundColor: '#333',
                    color: 'white',
                    border: '1px solid #555',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                  }}
                >
                  {t('common.reset', { defaultValue: 'Reset' })}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Table ── */}
        <div
          style={{
            backgroundColor: '#1b3350',
            borderRadius: '12px',
            border: '1px solid #333',
            overflow: 'hidden',
          }}
        >
          {paginatedCurrencies && paginatedCurrencies.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: '#1a1f2e',
                      borderBottom: '2px solid #AC2000',
                    }}
                  >
                    <th onClick={() => handleSort(SORT_FIELDS.index)} style={thSortable}>
                      {t('common.serialNumber', { defaultValue: '#' })}{' '}
                      {getSortIcon(SORT_FIELDS.index)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.name)} style={thSortable}>
                      {t('currencies.table.name', { defaultValue: 'Name' })}{' '}
                      {getSortIcon(SORT_FIELDS.name)}
                    </th>
                    <th onClick={() => handleSort(SORT_FIELDS.code)} style={thSortable}>
                      {t('currencies.table.code', { defaultValue: 'Code' })}{' '}
                      {getSortIcon(SORT_FIELDS.code)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.symbol)}
                      style={thSortable}
                    >
                      {t('currencies.table.symbol', { defaultValue: 'Symbol' })}{' '}
                      {getSortIcon(SORT_FIELDS.symbol)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.status)}
                      style={thSortable}
                    >
                      {t('currencies.table.status', { defaultValue: 'Status' })}{' '}
                      {getSortIcon(SORT_FIELDS.status)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.isBase)}
                      style={thSortable}
                    >
                      {t('currencies.table.isBase', { defaultValue: 'Base Currency' })}{' '}
                      {getSortIcon(SORT_FIELDS.isBase)}
                    </th>
                    <th style={{ ...thSortable, cursor: 'default' }}>
                      {t('currencies.table.actions', { defaultValue: 'Actions' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCurrencies.map((currency, index) => {
                    const recordIndex = (currentPageSafe - 1) * perPage + index + 1;
                    return (
                      <tr
                        key={currency.id}
                        style={{
                          borderBottom: '2px solid #444',
                          borderTop: index === 0 ? '2px solid #444' : 'none',
                        }}
                      >
                        <td style={tdCenter}>{recordIndex}</td>
                        <td
                          style={{
                            padding: '15px',
                            textAlign,
                            color: 'white',
                            direction: dir,
                            verticalAlign: 'middle',
                          }}
                        >
                          {currency.name ||
                            t('currencies.notAvailable', { defaultValue: '—' })}
                        </td>
                        <td style={tdCenter}>
                          {currency.code ||
                            t('currencies.notAvailable', { defaultValue: '—' })}
                        </td>
                        <td style={tdCenter}>
                          {currency.symbol ||
                            t('currencies.notAvailable', { defaultValue: '—' })}
                        </td>
                        <td style={{ ...tdCenter, color: undefined }}>
                          <span
                            style={{
                              padding: '5px 15px',
                              borderRadius: '20px',
                              backgroundColor: currency.is_active
                                ? 'rgba(172,32,0,0.4)'
                                : 'rgba(220,53,69,0.4)',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 'bold',
                            }}
                          >
                            {currencyStatus(currency)}
                          </span>
                        </td>
                        <td style={{ ...tdCenter, color: undefined }}>
                          <span
                            style={{
                              padding: '5px 15px',
                              borderRadius: '20px',
                              backgroundColor: currency.is_base_currency
                                ? 'rgba(0,123,255,0.4)'
                                : 'rgba(102,102,102,0.4)',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 'bold',
                            }}
                          >
                            {currencyIsBase(currency)}
                          </span>
                        </td>
                        <td style={{ padding: '15px' }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              justifyContent: 'center',
                            }}
                          >
                            <IconButton
                              bg="#666"
                              onClick={() => pdf.previewSingle(currency)}
                              title={t('currencies.actions.view', {
                                defaultValue: 'View Details',
                              })}
                            >
                              <FaEye />
                            </IconButton>
                            <IconButton
                              bg="#1f5ff1"
                              onClick={() =>
                                pdf.printSingle(currency, currency.name || currency.id)
                              }
                              disabled={pdf.isGeneratingPdf}
                              title={t('currencies.actions.print', {
                                defaultValue: 'Print',
                              })}
                            >
                              <FaPrint />
                            </IconButton>
                            <IconButton
                              bg="#28a745"
                              onClick={() => handleExportCurrencyToExcel(currency)}
                              title={t('currencies.actions.export', {
                                defaultValue: 'Export Excel',
                              })}
                            >
                              <FaFileExcel />
                            </IconButton>
                            <IconButton
                              bg="#17a2b8"
                              onClick={() => navigate(`/currencies/edit/${currency.id}`)}
                              title={t('currencies.actions.edit', {
                                defaultValue: 'Edit',
                              })}
                            >
                              <FaEdit />
                            </IconButton>
                            <IconButton
                              bg="#c0392b"
                              onClick={() => handleDelete(currency)}
                              title={t('currencies.actions.delete', {
                                defaultValue: 'Delete',
                              })}
                            >
                              <FaTrash />
                            </IconButton>
                            <IconButton
                              bg="#34495e"
                              onClick={() => handleToggleStatus(currency.id)}
                              title={t('currencies.actions.toggleStatus', {
                                defaultValue: 'Toggle Status',
                              })}
                            >
                              {currency.is_active ? <FaToggleOn /> : <FaToggleOff />}
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {t('currencies.noData', { defaultValue: 'No currencies found' })}
            </div>
          )}

          {/* ── Pagination ── */}
          {totalPages > 1 &&
            (() => {
              const pages = [];
              for (let i = 1; i <= totalPages; i += 1) {
                if (
                  i === 1 ||
                  i === totalPages ||
                  (i >= currentPageSafe - 2 && i <= currentPageSafe + 2)
                ) {
                  pages.push(i);
                } else if (
                  i === currentPageSafe - 3 ||
                  i === currentPageSafe + 3
                ) {
                  pages.push('...');
                }
              }
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '20px',
                    paddingBottom: '20px',
                    borderTop: '1px solid #333',
                    paddingTop: '20px',
                  }}
                >
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                    disabled={currentPageSafe === 1}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('common.previous', { defaultValue: 'Previous' })}
                  </button>
                  {pages.map((p, idx) =>
                    p === '...' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        style={{ padding: '8px', color: 'white' }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor:
                            p === currentPageSafe ? '#AC2000' : '#1a1f2e',
                          color: 'white',
                          border: '1px solid #333',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                    }
                    disabled={currentPageSafe === totalPages}
                    style={{
                      padding: '8px 12px',
                      backgroundColor:
                        currentPageSafe === totalPages ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor:
                        currentPageSafe === totalPages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('common.next', { defaultValue: 'Next' })}
                  </button>
                </div>
              );
            })()}
        </div>

        {/* ── Delete Modal ── */}
        {showDeleteModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 999999,
            }}
          >
            <div
              style={{
                backgroundColor: '#1a1f2e',
                padding: '30px',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '400px',
                border: '1px solid #333',
                direction: dir,
              }}
            >
              <h2
                style={{
                  color: 'white',
                  marginBottom: '20px',
                  fontSize: '20px',
                  textAlign,
                }}
              >
                {t('currencies.delete.title', { defaultValue: 'Confirm Delete' })}
              </h2>
              <p style={{ color: '#ccc', marginBottom: '30px', textAlign }}>
                {t('currencies.delete.confirm', {
                  defaultValue: 'Are you sure you want to delete the currency',
                })}{' '}
                "{currencyToDelete?.name}"?
              </p>
              <p
                style={{
                  color: '#dc3545',
                  fontSize: '13px',
                  marginBottom: '20px',
                  textAlign,
                }}
              >
                {t('currencies.delete.irreversible', {
                  defaultValue: 'This action cannot be undone.',
                })}
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCurrencyToDelete(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {t('currencies.delete.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {t('currencies.delete.confirmButton', { defaultValue: 'Delete' })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Shared PDF Engine — measurement, preview, print & single-record */}
      <PdfExportManager
        pdf={pdf}
        title={t('currencies.title', { defaultValue: 'Currencies' })}
        columns={exportColumns}
        rows={filteredCurrencies}
        filtersSummary={filtersSummary}
        singleTitle={t('currencies.print.title', {
          defaultValue: 'Currency Details',
        })}
        renderSingle={renderSingleCurrency}
      />
    </>
  );
};

const thSortable = {
  padding: '15px',
  textAlign: 'center',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '16px',
  borderBottom: '1px solid #333',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

const tdCenter = {
  padding: '15px',
  textAlign: 'center',
  color: 'white',
  verticalAlign: 'middle',
};

const IconButton = ({ children, onClick, title, bg, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      padding: '8px',
      borderRadius: '6px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '14px',
      opacity: disabled ? 0.7 : 1,
    }}
  >
    {children}
  </button>
);

export default Currencies;
